import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { useCart } from "../lib/CartContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ExternalLink, Copy, Tag, X } from "lucide-react";
import { useCustomerAuth } from "../lib/CustomerAuthContext";

const PAYPAL_LINK = process.env.REACT_APP_PAYPAL_LINK || "https://www.paypal.com/ncp/payment/R9F2CLJ5WJFVU";

const CartPage = () => {
  const { items, remove, updateQty, subtotal, clear } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("cart");
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_discord: "",
    shipping_line1: "",
    shipping_line2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_postal: "",
    shipping_country: "",
    notes: "",
  });

  // Pre-fill from signed-in customer once
  useEffect(() => {
    if (customer && !form.customer_email) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.name || "",
        customer_email: f.customer_email || customer.email || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Enter a code"); return; }
    setCouponLoading(true);
    try {
      const emailForLookup = customer?.email || form.customer_email || null;
      const { data } = await api.post(
        "/coupons/validate",
        { code, subtotal, customer_email: emailForLookup },
        { withCredentials: true },
      );
      setCoupon(data);
      setCouponInput("");
      toast.success(`${data.code} applied — -$${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    toast("Coupon removed");
  };

  const discountAmount = coupon ? coupon.discount_amount : 0;
  const total = Math.max(0, subtotal - discountAmount);

  const placeOrder = async (e) => {
    e?.preventDefault();
    const required = ["customer_name", "customer_email", "shipping_line1", "shipping_city", "shipping_postal", "shipping_country"];
    for (const k of required) {
      if (!form[k]) { toast.error("Please fill all required fields"); return; }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity })),
        coupon_code: coupon?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      setSubmitted(data);
      setStep("submitted");
      clear();
      // Open PayPal in a new tab
      window.open(PAYPAL_LINK, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setLoading(false);
    }
  };

  const copyAmount = () => {
    if (!submitted) return;
    navigator.clipboard?.writeText(submitted.total.toFixed(2));
    toast.success("Amount copied");
  };

  // ----- Submitted view -----
  if (step === "submitted" && submitted) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="border-2 border-zinc-800 bg-zinc-950 p-8 brutal-shadow" data-testid="order-submitted">
            <div className="font-mono-tech text-xs text-lime-500 mb-2">[ ORDER PLACED // {submitted.order_number} ]</div>
            <h1 className="font-display text-4xl sm:text-5xl text-zinc-50 uppercase leading-none">Almost there.</h1>
            <p className="mt-4 text-zinc-300">Your order is saved. Now send <span className="text-zinc-200 font-mono-tech">EXACTLY</span> the amount below via PayPal so we can match the payment to your order.</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border-2 border-zinc-800 p-4 bg-zinc-900">
                <div className="font-mono-tech text-xs uppercase text-zinc-500 mb-1">Send this amount</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display text-4xl text-zinc-200" data-testid="submitted-amount">${submitted.total.toFixed(2)}</div>
                  <button type="button" onClick={copyAmount} className="border-2 border-zinc-700 hover:border-zinc-200 text-zinc-300 hover:text-zinc-200 p-2" data-testid="copy-amount-btn"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="border-2 border-zinc-800 p-4 bg-zinc-900">
                <div className="font-mono-tech text-xs uppercase text-zinc-500 mb-1">Put in PayPal note</div>
                <div className="font-mono-tech text-sm text-lime-400">Order #{submitted.order_number}</div>
                <div className="font-mono-tech text-xs text-zinc-400 mt-1">+ your email so we can confirm</div>
              </div>
            </div>

            <a
              href={PAYPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-paypal-btn"
              className="mt-6 inline-flex items-center gap-2 bg-zinc-300 hover:bg-zinc-200 text-zinc-950 px-6 py-3 font-display text-lg tracking-widest uppercase brutal-shadow-orange hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all"
            >
              Open PayPal Again <ExternalLink className="w-4 h-4" />
            </a>

            <div className="mt-8 border-t-2 border-zinc-800 pt-6 font-mono-tech text-xs text-zinc-400 uppercase space-y-1">
              <div>NEXT // Once paid, we&apos;ll confirm via {submitted.customer_email}</div>
              <div>SUPPORT // Contact us with order #{submitted.order_number} if any issue</div>
            </div>

            <div className="mt-6 flex gap-3">
              <Link to="/shop" className="font-mono-tech text-xs uppercase text-zinc-400 hover:text-zinc-200" data-testid="continue-shopping">Continue Shopping →</Link>
              <Link to="/contact" className="font-mono-tech text-xs uppercase text-zinc-400 hover:text-zinc-200">Contact Us</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ----- Empty cart -----
  if (items.length === 0 && step === "cart") {
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="font-mono-tech text-xs text-zinc-200 mb-2">[ CART // CHECKOUT ]</div>
          <h1 className="font-display text-5xl sm:text-7xl text-zinc-50 uppercase leading-none">Inventory</h1>
          <div className="mt-10 border-2 border-zinc-800 p-12 text-center" data-testid="empty-cart">
            <p className="font-mono-tech text-zinc-500 uppercase text-sm">// Your cart is empty</p>
            <Link to="/shop" className="mt-4 inline-block font-display text-zinc-200 text-xl uppercase tracking-widest" data-testid="empty-cart-shop-link">Browse the Shop →</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ----- Cart + checkout form -----
  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="font-mono-tech text-xs text-zinc-200 mb-2">[ CART // CHECKOUT ]</div>
        <h1 className="font-display text-5xl sm:text-7xl text-zinc-50 uppercase leading-none">Inventory</h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Items + Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-4" data-testid="cart-items">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}-${it.color || "_"}`} className="border-2 border-zinc-800 bg-zinc-950 p-4 flex gap-4" data-testid={`cart-item-${it.slug}`}>
                  <div className="w-24 h-24 bg-zinc-900 flex-shrink-0">
                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <Link to={`/shop/${it.slug}`} className="font-display text-xl text-zinc-50 uppercase tracking-wider hover:text-zinc-200">{it.name}</Link>
                        <div className="font-mono-tech text-xs text-zinc-500 uppercase mt-1">
                          Size: {it.size}{it.color ? ` // Color: ${it.color}` : ""}
                        </div>
                      </div>
                      <div className="font-mono-tech text-sm text-zinc-200">${(it.price * it.quantity).toFixed(2)}</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border-2 border-zinc-800">
                        <button type="button" onClick={() => updateQty(it.product_id, it.size, it.color, it.quantity - 1)} className="px-2 py-1 text-zinc-200 hover:bg-zinc-900" data-testid={`dec-${it.slug}`}><Minus className="w-3 h-3" /></button>
                        <div className="px-3 py-1 font-mono-tech text-sm text-zinc-100">{it.quantity}</div>
                        <button type="button" onClick={() => updateQty(it.product_id, it.size, it.color, it.quantity + 1)} className="px-2 py-1 text-zinc-200 hover:bg-zinc-900" data-testid={`inc-${it.slug}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <button type="button" onClick={() => remove(it.product_id, it.size, it.color)} className="font-mono-tech text-xs uppercase text-zinc-400 hover:text-red-500 flex items-center gap-1" data-testid={`remove-${it.slug}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={clear} className="font-mono-tech text-xs uppercase text-zinc-500 hover:text-red-500" data-testid="clear-cart">Clear cart</button>
            </div>

            <form onSubmit={placeOrder} className="border-2 border-zinc-800 p-5 bg-zinc-950" data-testid="checkout-form">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-xs text-zinc-200">[ SHIPPING + CONTACT ]</div>
                {customer ? (
                  <div className="font-mono-tech text-[10px] uppercase text-zinc-500" data-testid="cart-signed-in-hint">
                    Signed in as {customer.email}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/account")}
                    className="font-mono-tech text-[10px] uppercase text-red-400 hover:text-red-300 link-underline"
                    data-testid="cart-signin-nudge"
                  >
                    Sign in for faster checkout →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full Name *"><input required value={form.customer_name} onChange={handle("customer_name")} className={fieldCls} data-testid="form-name" /></Field>
                <Field label="Email *"><input type="email" required value={form.customer_email} onChange={handle("customer_email")} className={fieldCls} data-testid="form-email" /></Field>
                <Field label="Discord Handle"><input value={form.customer_discord} onChange={handle("customer_discord")} className={fieldCls} placeholder="optional" data-testid="form-discord" /></Field>
                <Field label="Country *"><input required value={form.shipping_country} onChange={handle("shipping_country")} className={fieldCls} data-testid="form-country" /></Field>
                <div className="sm:col-span-2"><Field label="Address Line 1 *"><input required value={form.shipping_line1} onChange={handle("shipping_line1")} className={fieldCls} data-testid="form-line1" /></Field></div>
                <div className="sm:col-span-2"><Field label="Address Line 2"><input value={form.shipping_line2} onChange={handle("shipping_line2")} className={fieldCls} data-testid="form-line2" /></Field></div>
                <Field label="City *"><input required value={form.shipping_city} onChange={handle("shipping_city")} className={fieldCls} data-testid="form-city" /></Field>
                <Field label="State / Region"><input value={form.shipping_state} onChange={handle("shipping_state")} className={fieldCls} data-testid="form-state" /></Field>
                <Field label="ZIP / Postal Code *"><input required value={form.shipping_postal} onChange={handle("shipping_postal")} className={fieldCls} data-testid="form-postal" /></Field>
                <div className="sm:col-span-2"><Field label="Order Notes"><textarea rows={2} value={form.notes} onChange={handle("notes")} className={fieldCls} placeholder="Anything we should know" data-testid="form-notes" /></Field></div>
              </div>
            </form>
          </div>

          {/* RIGHT: Summary */}
          <aside className="border-2 border-zinc-800 bg-zinc-900/40 p-6 h-fit" data-testid="cart-summary">
            <h3 className="font-display text-3xl text-zinc-50 uppercase mb-4">Summary</h3>
            <div className="space-y-2 font-mono-tech text-sm">
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="text-zinc-100">${subtotal.toFixed(2)}</span></div>
              {coupon && (
                <div className="flex justify-between text-red-400" data-testid="coupon-line">
                  <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {coupon.code}{coupon.discount_type === "percent" ? ` (-${coupon.discount_value}%)` : ""}</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-zinc-400"><span>Shipping</span><span className="text-zinc-100">Quoted later</span></div>
            </div>

            {/* Coupon input */}
            <div className="mt-5" data-testid="coupon-block">
              {coupon ? (
                <button
                  type="button"
                  onClick={removeCoupon}
                  data-testid="remove-coupon-btn"
                  className="w-full flex items-center justify-between px-3 py-2 border-2 border-red-600/60 text-red-400 hover:border-red-500 hover:bg-red-600/10 transition-colors font-mono-tech text-xs uppercase tracking-widest"
                >
                  <span className="flex items-center gap-2"><Tag className="w-3 h-3" /> {coupon.code} applied</span>
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                    placeholder="COUPON CODE"
                    data-testid="coupon-input"
                    className="flex-1 bg-zinc-900 border-2 border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 font-mono-tech text-xs text-zinc-100 uppercase tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponInput.trim()}
                    data-testid="apply-coupon-btn"
                    className="px-4 py-2 btn-brand disabled:opacity-50 font-mono-tech text-xs uppercase tracking-widest"
                  >
                    {couponLoading ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <div className="border-t-2 border-zinc-800 my-4" />
            <div className="flex justify-between items-end mb-6">
              <div className="font-mono-tech text-xs uppercase text-zinc-400">Total</div>
              <div className="font-display text-3xl text-zinc-200" data-testid="cart-total">${total.toFixed(2)}</div>
            </div>
            <button
              type="button"
              onClick={placeOrder}
              disabled={loading}
              data-testid="place-order-btn"
              className="w-full bg-zinc-300 hover:bg-zinc-200 disabled:opacity-60 text-zinc-950 px-6 py-3 font-display text-lg tracking-widest uppercase brutal-shadow-orange hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all"
            >
              {loading ? "Placing order…" : "Place Order & Pay with PayPal"}
            </button>
            <p className="mt-3 font-mono-tech text-[10px] uppercase text-zinc-500 text-center">// We save your order, then redirect to PayPal</p>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const fieldCls = "w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-xs uppercase text-zinc-400 block mb-1">{label}</span>
    {children}
  </label>
);

export default CartPage;
